'use client'

import { CartItemList } from '@/feature/cart/components/cart-item-list/CartItemList'
import { addToCartDrawerDataAtom } from '@/feature/cart/services/cart.store'
import { LinkedProductsSlider } from '@/feature/product/components/linked-products-slider/LinkedProductsSlider.client'
import { ProductRowReadOnly } from '@/feature/product/components/product-row-read-only/ProductRowReadOnly.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { useRouter } from '@/lib/navigation/PageNavigation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { InfoCard } from '@/ui/cards/info-card/InfoCard'
import { Drawer, DrawerAction, DrawerBody, DrawerFooter } from '@/ui/drawer/drawer/Drawer.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { ClickAndCollectStatus } from '@gql-generated/graphql'
import { atom, useAtom } from 'jotai'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { toCheckoutCart } from '../../services/cart.routing'

export const isAddToCartDrawerVisible = atom(false)

export function AddToCartDrawer() {
  const { t } = useTranslation(['addToCartDrawer'])
  const [addToCartDrawerData, setAddToCartDrawerData] = useAtom(addToCartDrawerDataAtom)
  const previousPath = useRef<string | null>(null)
  const pathname = usePathname()
  const router = useRouter()

  useEffect(() => {
    const closeDrawer = () => {
      setAddToCartDrawerData((prev) => ({
        ...prev,
        isOpen: false,
      }))
    }

    if (previousPath.current === null || previousPath.current !== pathname) {
      closeDrawer()
    }

    previousPath.current = pathname
  }, [pathname, setAddToCartDrawerData])

  const handleRedirect = () => {
    pushToGtmDataLayer({
      event: 'gaEvent',
      eventCategory: 'fiche article / produit',
      eventAction: 'pop-in confirmation ajout panier',
      eventLabel: 'clic voir mon panier',
    })
    router.pushWithProgressBar(toCheckoutCart)
  }

  const mainProduct = addToCartDrawerData.cartItems[0]

  return (
    <Drawer
      size="xl"
      isOpen={addToCartDrawerData.isOpen}
      onOpenChange={(isOpen) => setAddToCartDrawerData((data) => ({ ...data, isOpen }))}
      title={t('addToCartDrawer:title', {
        count: addToCartDrawerData.cartItems.length,
      })}
    >
      <DrawerBody>
        <div className="flex flex-col gap-3">
          {addToCartDrawerData.clickAndCollectStatus ===
            ClickAndCollectStatus.ClickAndCollectOnly && (
            <InfoCard
              variant="warning"
              title={t('addToCartDrawer:clickAndCollectOnly.title')}
              content={t('addToCartDrawer:clickAndCollectOnly.description')}
            />
          )}
          {addToCartDrawerData.cartItems.length > 0 ? (
            <CartItemList withDividers className="w-full gap-2">
              {addToCartDrawerData.cartItems.map((cartItem, index) => (
                <CartItemList.Item key={cartItem.product?.urlKey || index}>
                  <ProductRowReadOnly
                    cartId={addToCartDrawerData.cartId}
                    cartItem={cartItem}
                    position={index + 1}
                  />
                </CartItemList.Item>
              ))}
            </CartItemList>
          ) : (
            <p>{t('addToCartDrawer:empty')}</p>
          )}
          {mainProduct?.product?.urlKey && addToCartDrawerData.cartItems.length === 1 && (
            <div className="w-full">
              <LinkedProductsSlider
                slug={mainProduct?.product?.urlKey}
                isAddableToFavorites={false}
                sliderClassName="px-2 desktop:px-3"
                source="produits complémentaires - pop-in ajout panier"
              />
            </div>
          )}
        </div>
      </DrawerBody>
      <DrawerFooter>
        <DrawerAction
          type="custom"
          handle="continue-purchase"
          variant="secondary"
          icon={<Icon.ArrowLeft />}
          action={() => {
            pushToGtmDataLayer({
              event: 'gaEvent',
              eventCategory: 'fiche article / produit',
              eventAction: 'pop-in confirmation ajout panier',
              eventLabel: 'clic continuer mes achats',
            })
          }}
        >
          {t('addToCartDrawer:buttons.continuePurchase')}
        </DrawerAction>
        <DrawerAction
          handle=""
          type="custom"
          action={handleRedirect}
          icon={<Icon.ShoppingBasket />}
          variant="primary"
        >
          {t('addToCartDrawer:buttons.toCart')}
        </DrawerAction>
      </DrawerFooter>
    </Drawer>
  )
}
